/**
 * OAuth Token Encryption Utility
 *
 * Provides client-side encryption for OAuth access tokens stored in secure storage.
 * Uses AES encryption with a session-based key to protect against XSS attacks.
 *
 * Security Model:
 * - Encryption key derived from session data (not stored permanently)
 * - Key regenerated on each login
 * - Encrypted tokens stored in secure storage (Keychain on iOS, Keystore on Android, localStorage on web)
 * - Decryption requires both encrypted token AND session key
 *
 * Note: This is not bulletproof against sophisticated XSS, but significantly
 * raises the bar for attackers compared to plain storage.
 */

import CryptoJS from 'crypto-js';
import { logger } from './logger';
import { getSecureStorage } from './storage';

// Session key storage key
const SESSION_KEY_STORAGE = 'nidonote_session_key';

// Session key cached in memory (restored from sessionStorage on load)
let sessionEncryptionKey: string | null = null;

// Initialize from sessionStorage on module load (survives page refresh)
if (typeof window !== 'undefined' && window.sessionStorage) {
  const storedKey = sessionStorage.getItem(SESSION_KEY_STORAGE);
  if (storedKey) {
    sessionEncryptionKey = storedKey;
    logger.auth('Session encryption key restored from sessionStorage');
  }
}

/**
 * Generate a new encryption key for the current session
 * Called on successful login
 */
export function generateSessionKey(): string {
  // Generate a random 256-bit key
  const key = CryptoJS.lib.WordArray.random(32).toString();
  sessionEncryptionKey = key;

  // Store in sessionStorage to survive page refresh
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.setItem(SESSION_KEY_STORAGE, key);
  }

  logger.auth('Session encryption key generated and stored');

  return key;
}

/**
 * Get the current session key
 * Returns null if not initialized (user not logged in)
 */
export function getSessionKey(): string | null {
  return sessionEncryptionKey;
}

/**
 * Clear the session key
 * Called on logout or session end
 */
export function clearSessionKey(): void {
  sessionEncryptionKey = null;

  // Clear from sessionStorage
  if (typeof window !== 'undefined' && window.sessionStorage) {
    sessionStorage.removeItem(SESSION_KEY_STORAGE);
  }

  logger.auth('Session encryption key cleared');
}

/**
 * Encrypt a token using the session key
 *
 * @param token - The OAuth access token to encrypt
 * @returns Encrypted token string
 * @throws Error if session key not initialized
 */
export function encryptToken(token: string): string {
  if (!sessionEncryptionKey) {
    throw new Error('Session encryption key not initialized. User must be logged in.');
  }

  try {
    // Encrypt the token with AES
    const encrypted = CryptoJS.AES.encrypt(token, sessionEncryptionKey).toString();

    logger.debug('TokenEncryption', 'Token encrypted successfully');

    return encrypted;
  } catch (error) {
    logger.error('TokenEncryption', 'Encryption failed:', error);
    throw new Error('Failed to encrypt token');
  }
}

/**
 * Decrypt a token using the session key
 *
 * @param encryptedToken - The encrypted token string
 * @returns Decrypted OAuth access token
 * @throws Error if session key not initialized or decryption fails
 */
export function decryptToken(encryptedToken: string): string {
  if (!sessionEncryptionKey) {
    throw new Error('Session encryption key not initialized. User must be logged in.');
  }

  try {
    // Decrypt the token
    const decrypted = CryptoJS.AES.decrypt(encryptedToken, sessionEncryptionKey);
    const token = decrypted.toString(CryptoJS.enc.Utf8);

    if (!token) {
      throw new Error('Decryption produced empty result');
    }

    logger.debug('TokenEncryption', 'Token decrypted successfully');

    return token;
  } catch (error) {
    logger.error('TokenEncryption', 'Decryption failed:', error);
    throw new Error('Failed to decrypt token');
  }
}

/**
 * Store an encrypted token in secure storage
 *
 * @param key - Storage key
 * @param token - OAuth access token to encrypt and store
 */
export async function storeEncryptedToken(key: string, token: string): Promise<void> {
  try {
    const encrypted = encryptToken(token);
    const storage = await getSecureStorage();
    await storage.setItem(key, encrypted);

    logger.debug('TokenEncryption', `Token stored encrypted in secure storage: ${key}`);
  } catch (error) {
    logger.error('TokenEncryption', `Failed to store encrypted token:`, error);
    throw error;
  }
}

/**
 * Retrieve and decrypt a token from secure storage
 *
 * @param key - Storage key
 * @returns Decrypted OAuth access token or null if not found
 */
export async function retrieveEncryptedToken(key: string): Promise<string | null> {
  try {
    const storage = await getSecureStorage();
    const encrypted = await storage.getItem(key);

    if (!encrypted) {
      return null;
    }

    const token = decryptToken(encrypted);

    logger.debug('TokenEncryption', `Token retrieved and decrypted from secure storage: ${key}`);

    return token;
  } catch (error) {
    logger.error('TokenEncryption', `Failed to retrieve encrypted token:`, error);

    // If decryption fails, remove the corrupted token
    const storage = await getSecureStorage();
    await storage.removeItem(key);

    return null;
  }
}

/**
 * Clear encrypted token from secure storage
 *
 * @param key - Storage key
 */
export async function clearEncryptedToken(key: string): Promise<void> {
  const storage = await getSecureStorage();
  await storage.removeItem(key);
  logger.debug('TokenEncryption', `Encrypted token cleared from secure storage: ${key}`);
}

/**
 * Check if encryption is initialized
 * Useful for debugging and error handling
 */
export function isEncryptionInitialized(): boolean {
  return sessionEncryptionKey !== null;
}
